import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import {
  extractDetail,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile, ExportFormat } from "@/modules/export";
import type { FinanceiroObrasColumnKey } from "./financeiro-obras.types";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { FINANCEIRO_EXPORT_COLUMN_KEYS } from "./financeiro-obras.constants";
import {
  DEFAULT_FINANCEIRO_OBRAS_SORT,
  EMPTY_FINANCEIRO_OBRAS_FILTERS,
  MAX_FINANCEIRO_OBRAS_PAGE_SIZE,
} from "./financeiro-obras.types";
import type {
  DetalheEscopoMacro,
  PlanejamentoControle,
} from "./financeiro-obras.planejamento.types";
import type {
  CancelarEscopoMacroRequest,
  CancelarEscopoMacroResposta,
  CancelarTipoObraRequest,
  CancelarTipoObraResposta,
  DadosCancelamentoTipoObra,
} from "./financeiro-obras.cancelamento.types";
import type {
  ObrasDestinoTransferencia,
  PreviaProjecaoTransferencia,
  PreviaProjecaoTransferenciaBody,
  TransferirEscopoMacroRequest,
  TransferirEscopoMacroResposta,
} from "./financeiro-obras.transferencia.types";
import type {
  ObrasDestinoTipo,
  ParametrosRevisaoTransferenciaTipo,
  PreviaProjecaoTransferenciaTipo,
  PreviaProjecaoTransferenciaTipoBody,
  RevisaoTransferenciaTipo,
  TransferirTipoObraRequest,
  TransferirTipoObraResposta,
} from "./financeiro-obras.transferencia-tipo.types";
import type {
  CriarEscopoMacroRequest,
  CriarEscopoMacroResposta,
  EquipamentosSfi,
  EscopoPadraoDoTipo,
  NomeComposto103,
  OrigemEscopoMacro,
  ParametrosNomeComposto,
} from "./financeiro-obras.escopo.types";
import type {
  NovoTipoObraRequest,
  NovoTipoObraResposta,
  OpcoesNovoTipoObra,
} from "./financeiro-obras.tipo-obra.types";
import type {
  FinanceiroEmbarcacaoOpcao,
  FinanceiroObraItem,
  FinanceiroObraTotalPorStatus,
  FinanceiroObrasFilters,
  FinanceiroObrasPage,
  FinanceiroObrasSort,
  FinanceiroTipoObra,
  FinanceiroTipoObraOpcao,
} from "./financeiro-obras.types";

/** Contrato consumido do hub-bff (HU 2670), privilégio `hub.financeiro:visualizar-painel`; somente `GET`. */
export const FINANCEIRO_OBRAS_PATHS = {
  LISTAR: "/api/financeiro/obras",
  /** Exportar Visualização: `GET` binário, privilégio `hub.financeiro:visualizar-painel`. */
  EXPORTAR: "/api/financeiro/obras/exportacao",
  /** Opções do filtro Embarcação, privilégio `hub.financeiro:visualizar-painel`. */
  EMBARCACOES: "/api/financeiro/obras/embarcacoes",
  /** Opções do filtro Tipo, privilégio `hub.financeiro:visualizar-painel`. */
  TIPOS_OBRA: "/api/financeiro/obras/tipos-obra",
  /** Planejamento e Controle da obra (HU 2709), privilégio `hub.financeiro:visualizar-detalhe`. */
  PLANEJAMENTO_CONTROLE: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/planejamento-controle`,
  PLANEJAMENTO_CONTROLE_EXPORTACAO: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/planejamento-controle/exportacao`,
  /** Detalhe somente leitura de um Escopo Macro da obra (HU 2709), mesmo privilégio. */
  ESCOPO_MACRO: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}`,
  /** Opções do Novo Tipo de Obra (`GET .../opcoes`) e inclusão (`POST`) na obra (HU 2715), privilégio `hub.financeiro:criar-tipo`. */
  TIPOS_OBRA_DA_OBRA: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra`,
  ORIGENS_ESCOPO_MACRO: "/api/financeiro/obras/escopos-macro/origens",
  ESCOPOS_PADRAO_DO_TIPO: (tipoObraId: string) =>
    `/api/obras/tipos-obra/${encodeURIComponent(tipoObraId)}/escopos-macro`,
  EQUIPAMENTOS_SFI: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/equipamentos-sfi`,
  NOME_COMPOSTO_103: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/nome-composto`,
  ESCOPOS_MACRO_DA_OBRA: (obraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro`,
  /** Cancelamento de um Escopo Macro (`POST`, HU 2714), privilégio `hub.financeiro:editar-detalhe`. */
  CANCELAR_ESCOPO_MACRO: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}/cancelamentos`,
  /** Dados do cancelamento de um Tipo de Obra (`GET`, HU 2717), privilégio `hub.financeiro:editar-detalhe`. */
  CANCELAMENTO_TIPO_OBRA_DADOS: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/cancelamento`,
  /** Obras da mesma embarcação como destino da transferência do Escopo (`GET`, HU 2713), privilégio `hub.financeiro:editar-detalhe`. */
  OBRAS_DESTINO_TRANSFERENCIA: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}/transferencia/obras-destino`,
  OBRAS_DESTINO_TRANSFERENCIA_TIPO: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/transferencia/obras-destino`,
  REVISAO_TRANSFERENCIA_TIPO: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/transferencia/revisao`,
  TRANSFERIR_TIPO_OBRA: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/transferencias`,
  /** Solicitação de transferência do Escopo (`POST`, HU 2713), mesmo privilégio. */
  TRANSFERIR_ESCOPO_MACRO: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}/transferencias`,
  PREVIA_PROJECAO_TRANSFERENCIA: (obraId: string, obraEscopoMacroId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/escopos-macro/${encodeURIComponent(obraEscopoMacroId)}/transferencia/projecao-revisada`,
  PREVIA_PROJECAO_TRANSFERENCIA_TIPO: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/transferencia/projecao-revisada`,
  /** Solicitação do cancelamento de um Tipo de Obra (`POST`, HU 2717), mesmo privilégio. */
  CANCELAR_TIPO_OBRA: (obraId: string, obraTipoObraId: string) =>
    `/api/financeiro/obras/${encodeURIComponent(obraId)}/tipos-obra/${encodeURIComponent(obraTipoObraId)}/cancelamentos`,
} as const;

export interface ListFinanceiroObrasParams {
  busca?: string;
  filters?: Partial<FinanceiroObrasFilters>;
  page?: number;
  pageSize?: number;
  sort?: FinanceiroObrasSort;
  signal?: AbortSignal;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function cleanValues(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter((value) => value !== "");
}

/**
 * Valida o período antes de consultar: só segue data `AAAA-MM-DD` e, com as duas, início não
 * posterior ao fim (período inválido não gera requisição com dados inválidos).
 */
export function sanitizePeriodo(filters: Partial<FinanceiroObrasFilters>): {
  dataInicio?: string;
  dataFim?: string;
} {
  const inicio = filters.dataInicio?.trim() ?? "";
  const fim = filters.dataFim?.trim() ?? "";
  const validInicio = ISO_DATE.test(inicio) ? inicio : undefined;
  const validFim = ISO_DATE.test(fim) ? fim : undefined;

  if (validInicio && validFim && validInicio > validFim) return {};

  return { dataInicio: validInicio, dataFim: validFim };
}

function buildParams(
  busca: string | undefined,
  filters: Partial<FinanceiroObrasFilters>,
): Record<string, HttpQueryParam> {
  const params: Record<string, HttpQueryParam> = {};

  const term = busca?.trim();
  if (term) params.busca = term;

  const statusFinanceiro = cleanValues(filters.statusFinanceiro);
  if (statusFinanceiro.length > 0) params.statusFinanceiro = statusFinanceiro;
  const embarcacaoId = cleanValues(filters.embarcacaoId);
  if (embarcacaoId.length > 0) params.embarcacaoId = embarcacaoId;
  const tipoObra = cleanValues(filters.tipoObra);
  if (tipoObra.length > 0) params.tipoObra = tipoObra;
  const condicao = cleanValues(filters.condicao);
  if (condicao.length > 0) params.condicao = condicao;

  const { dataInicio, dataFim } = sanitizePeriodo(filters);
  if (dataInicio) params.dataInicio = dataInicio;
  if (dataFim) params.dataFim = dataFim;

  return params;
}

function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toTipoObra(raw: unknown): FinanceiroTipoObra | null {
  if (typeof raw === "string") {
    const codigo = raw.trim();
    return codigo ? { codigo, nome: "" } : null;
  }
  if (!isRecord(raw)) return null;

  const codigo = readFirstText(raw, "codigo").trim();
  return codigo ? { codigo, nome: readFirstText(raw, "nome").trim() } : null;
}

function toFinanceiroObraItem(raw: unknown): FinanceiroObraItem | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  const embarcacao = isRecord(raw.embarcacao) ? raw.embarcacao : {};
  const status = isRecord(raw.statusFinanceiro) ? raw.statusFinanceiro : null;
  const statusCodigo = status ? readFirstText(status, "codigo").trim() : "";

  return {
    id,
    codigo: readFirstText(raw, "codigo").trim() || id,
    embarcacao: {
      id: readFirstText(embarcacao, "id").trim(),
      nome: readFirstText(embarcacao, "nome").trim(),
      acronimo: readFirstText(embarcacao, "acronimo").trim() || null,
    },
    statusFinanceiro:
      status && statusCodigo
        ? { codigo: statusCodigo, descricao: readFirstText(status, "descricao").trim() }
        : null,
    tiposObra: Array.isArray(raw.tiposObra)
      ? raw.tiposObra.map(toTipoObra).filter((tipo): tipo is FinanceiroTipoObra => tipo !== null)
      : [],
    inicio: readFirstText(raw, "inicio").trim(),
    fim: readFirstText(raw, "fim").trim(),
    duracaoDias: readNumber(raw, "duracaoDias"),
    // Campos opcionais: o core ainda não os envia; ausente ou inválido vira `null` (a tela mostra "-").
    condicao: readFirstText(raw, "condicao").trim() || null,
    realizadoBrlMilhoes: readNumber(raw, "realizadoBrlMilhoes"),
    outlookBrlMilhoes: readNumber(raw, "outlookBrlMilhoes"),
    realizadoPercentual: readNumber(raw, "realizadoPercentual"),
    empenhadoPercentual: readNumber(raw, "empenhadoPercentual"),
    comprometidoPercentual: readNumber(raw, "comprometidoPercentual"),
    naoComprometidoPercentual: readNumber(raw, "naoComprometidoPercentual"),
    estimadoPercentual: readNumber(raw, "estimadoPercentual"),
  };
}

function toTotalPorStatus(raw: unknown): FinanceiroObraTotalPorStatus | null {
  if (!isRecord(raw) || typeof raw.total !== "number") return null;

  return {
    statusFinanceiro: readFirstText(raw, "statusFinanceiro").trim() || null,
    total: raw.total,
  };
}

/**
 * HU 2670 — consulta de obras do Financeiro (privilégio `hub.financeiro:visualizar-painel`). Filtros de lista
 * como chave repetida, `dataInicio`/`dataFim` e o contrato paginado (`page`, `pageSize`, `sort_by`, `sort_direction`; padrão
 * 20, máximo 100); devolve também o total por status. Os Cards pedem uma coluna por vez
 * (`statusFinanceiro` único, `pageSize` 10).
 */
export async function listFinanceiroObras(
  request: ListFinanceiroObrasParams = {},
): Promise<FinanceiroObrasPage> {
  const {
    busca,
    filters = EMPTY_FINANCEIRO_OBRAS_FILTERS,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = DEFAULT_FINANCEIRO_OBRAS_SORT,
    signal,
  } = request;

  const params = buildParams(busca, filters);

  Object.assign(
    params,
    toPaginatedQuery({
      page: Math.max(1, Math.trunc(page)),
      pageSize: Math.min(MAX_FINANCEIRO_OBRAS_PAGE_SIZE, Math.max(1, Math.trunc(pageSize))),
      sort,
    }),
  );

  const response = await getHttpClient().get<unknown>(FINANCEIRO_OBRAS_PATHS.LISTAR, {
    signal,
    params,
  });

  const items = extractItems(response.data)
    .map(toFinanceiroObraItem)
    .filter((item): item is FinanceiroObraItem => item !== null);
  const detail = extractDetail(response.data);
  const totais = isRecord(detail) ? detail.totaisPorStatus : undefined;

  return {
    items,
    total: extractTotal(detail) ?? items.length,
    totaisPorStatus: Array.isArray(totais)
      ? totais
          .map(toTotalPorStatus)
          .filter((total): total is FinanceiroObraTotalPorStatus => total !== null)
      : [],
  };
}

export interface ExportFinanceiroObrasParams {
  busca?: string;
  filters?: Partial<FinanceiroObrasFilters>;
  /** Rótulos legíveis dos filtros aplicados, para a linha "Filtros:" do arquivo. */
  filtros?: string[];
  sort?: FinanceiroObrasSort;
  /** Colunas visíveis da tela, na ordem; o gateway as traduz para as chaves do core. */
  colunas: readonly FinanceiroObrasColumnKey[];
  format: ExportFormat;
  signal?: AbortSignal;
}

/**
 * HU 2670 — exporta a visão atual (mesmos filtros e ordenação de `listFinanceiroObras`, sem
 * paginação, só as colunas visíveis na ordem da tela). O arquivo é gerado no servidor
 * (privilégio `hub.financeiro:visualizar-painel`); o nome vem do `Content-Disposition`.
 */
export async function exportFinanceiroObras({
  busca,
  filters = EMPTY_FINANCEIRO_OBRAS_FILTERS,
  filtros,
  sort = DEFAULT_FINANCEIRO_OBRAS_SORT,
  colunas,
  format,
  signal,
}: ExportFinanceiroObrasParams): Promise<ExportedFile> {
  const params = buildParams(busca, filters);

  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendExportColumns(params, colunas, FINANCEIRO_EXPORT_COLUMN_KEYS, "Financeiro > Obras");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(FINANCEIRO_OBRAS_PATHS.EXPORTAR, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

export interface LookupRequestOptions {
  signal?: AbortSignal;
}

/** Embarcações para o filtro, na ordem da API (por nome). */
export async function listFinanceiroEmbarcacoes({ signal }: LookupRequestOptions = {}): Promise<
  FinanceiroEmbarcacaoOpcao[]
> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_OBRAS_PATHS.EMBARCACOES, {
    signal,
  });

  return extractItems(response.data)
    .map((raw): FinanceiroEmbarcacaoOpcao | null => {
      if (!isRecord(raw)) return null;
      const id = readFirstText(raw, "id").trim();
      return id ? { id, nome: readFirstText(raw, "nome").trim() || id } : null;
    })
    .filter((item): item is FinanceiroEmbarcacaoOpcao => item !== null);
}

/** Tipos de obra para o filtro, na ordem da API (por código). */
export async function listFinanceiroTiposObra({ signal }: LookupRequestOptions = {}): Promise<
  FinanceiroTipoObraOpcao[]
> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_OBRAS_PATHS.TIPOS_OBRA, {
    signal,
  });

  return extractItems(response.data)
    .map((raw): FinanceiroTipoObraOpcao | null => {
      if (!isRecord(raw)) return null;
      const id = readFirstText(raw, "id").trim();
      if (!id) return null;
      const codigo = readFirstText(raw, "codigo").trim();
      return { id, codigo, nome: readFirstText(raw, "nome").trim() || codigo || id };
    })
    .filter((item): item is FinanceiroTipoObraOpcao => item !== null);
}

/** Corpo `data` do envelope, tipado pelo contrato; resposta sem objeto é falha de contrato, não dado vazio. */
export function readEnvelopeData<T>(payload: unknown): T {
  const data = isRecord(payload) && "data" in payload ? payload.data : payload;
  if (!isRecord(data)) throw new Error("Resposta inválida do bff.");
  return data as T;
}

/** HU 2709 — composição Centro de Custo → Tipo de Obra → Escopo Macro e Controle do Outlook da obra. */
export async function getPlanejamentoControle(
  obraId: string,
  signal?: AbortSignal,
): Promise<PlanejamentoControle> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.PLANEJAMENTO_CONTROLE(obraId),
    { signal },
  );

  const dados = readEnvelopeData<Omit<PlanejamentoControle, "meta">>(response.data);
  if (!Array.isArray(dados.linhas)) {
    throw new Error("Resposta inválida do bff: planejamento sem linhas.");
  }
  const meta = isRecord(response.data) && isRecord(response.data.meta) ? response.data.meta : {};

  return {
    ...dados,
    meta: {
      tiposSemCentroCusto: Number(meta.tiposSemCentroCusto) || 0,
      escoposSemTipo: Number(meta.escoposSemTipo) || 0,
    },
  };
}

export async function exportPlanejamentoControle(
  obraId: string,
  format: ExportFormat,
  signal?: AbortSignal,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    FINANCEIRO_OBRAS_PATHS.PLANEJAMENTO_CONTROLE_EXPORTACAO(obraId),
    { signal, params: { formato: format }, responseType: "arraybuffer" },
  );

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** HU 2709 — detalhe somente leitura do Escopo Macro (parâmetros, coletores e mensalização). */
export async function getDetalheEscopoMacro(
  obraId: string,
  obraEscopoMacroId: string,
  signal?: AbortSignal,
): Promise<DetalheEscopoMacro> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.ESCOPO_MACRO(obraId, obraEscopoMacroId),
    { signal },
  );

  return readEnvelopeData<DetalheEscopoMacro>(response.data);
}

/** HU 2715 — Centros de Custo e Tipos de Obra elegíveis, com o dominante atual (opções do modal Novo Tipo de Obra). */
export async function getOpcoesNovoTipoObra(
  obraId: string,
  signal?: AbortSignal,
): Promise<OpcoesNovoTipoObra> {
  const response = await getHttpClient().get<unknown>(
    `${FINANCEIRO_OBRAS_PATHS.TIPOS_OBRA_DA_OBRA(obraId)}/opcoes`,
    { signal },
  );

  return readEnvelopeData<OpcoesNovoTipoObra>(response.data);
}

export interface OpcoesMutacao {
  idempotencyKey?: string;
}

export const cabecalhoIdempotencia = (idempotencyKey?: string) =>
  idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined;

/** HU 2715 — inclui o Tipo de Obra no Centro de Custo; 409 (duplicado, excludente, CC inelegível) sobe como `APIErrorException`. */
export async function criarTipoObra(
  obraId: string,
  body: NovoTipoObraRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<NovoTipoObraResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.TIPOS_OBRA_DA_OBRA(obraId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  return readEnvelopeData<NovoTipoObraResposta>(response.data);
}

/** HU 2714 — solicita o cancelamento do Escopo; 409 (já pendente ou cancelado) sobe como `APIErrorException`. */
export async function cancelarEscopoMacro(
  obraId: string,
  obraEscopoMacroId: string,
  body: CancelarEscopoMacroRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CancelarEscopoMacroResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.CANCELAR_ESCOPO_MACRO(obraId, obraEscopoMacroId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  return readEnvelopeData<CancelarEscopoMacroResposta>(response.data);
}

/** HU 2717 — escopos abrangidos, sucessão do dominante e se o Tipo é o último da obra. */
export async function getCancelamentoTipoObra(
  obraId: string,
  obraTipoObraId: string,
  signal?: AbortSignal,
): Promise<DadosCancelamentoTipoObra> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.CANCELAMENTO_TIPO_OBRA_DADOS(obraId, obraTipoObraId),
    { signal },
  );

  return readEnvelopeData<DadosCancelamentoTipoObra>(response.data);
}

/** HU 2717 — solicita o cancelamento do Tipo e dos seus Escopos; 409 (Tipo não aprovado ou com pendência) sobe como `APIErrorException`. */
export async function cancelarTipoObra(
  obraId: string,
  obraTipoObraId: string,
  body: CancelarTipoObraRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CancelarTipoObraResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.CANCELAR_TIPO_OBRA(obraId, obraTipoObraId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  return readEnvelopeData<CancelarTipoObraResposta>(response.data);
}

/** HU 2713 — Obras da mesma embarcação com a disponibilidade de cada uma; escopo sem Tipo vigente sobe como 400 (`APIErrorException`). */
export async function getObrasDestinoTransferencia(
  obraId: string,
  obraEscopoMacroId: string,
  signal?: AbortSignal,
): Promise<ObrasDestinoTransferencia> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.OBRAS_DESTINO_TRANSFERENCIA(obraId, obraEscopoMacroId),
    { signal },
  );

  return readEnvelopeData<ObrasDestinoTransferencia>(response.data);
}

/** HU 2713 — solicita a transferência; 400 (destino indisponível, período) e 409 (pendência no Escopo ou no Tipo) sobem como `APIErrorException`. */
export async function transferirEscopoMacro(
  obraId: string,
  obraEscopoMacroId: string,
  body: TransferirEscopoMacroRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<TransferirEscopoMacroResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.TRANSFERIR_ESCOPO_MACRO(obraId, obraEscopoMacroId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  return readEnvelopeData<TransferirEscopoMacroResposta>(response.data);
}

export async function getObrasDestinoTransferenciaTipo(
  obraId: string,
  obraTipoObraId: string,
  { signal }: LookupRequestOptions = {},
): Promise<ObrasDestinoTipo> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.OBRAS_DESTINO_TRANSFERENCIA_TIPO(obraId, obraTipoObraId),
    { signal },
  );

  return readEnvelopeData<ObrasDestinoTipo>(response.data);
}

export async function getRevisaoTransferenciaTipo(
  obraId: string,
  obraTipoObraId: string,
  { obraDestinoId, centroCustoDestinoId }: ParametrosRevisaoTransferenciaTipo,
  { signal }: LookupRequestOptions = {},
): Promise<RevisaoTransferenciaTipo> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.REVISAO_TRANSFERENCIA_TIPO(obraId, obraTipoObraId),
    { signal, params: { obraDestinoId, centroCustoDestinoId } },
  );

  return readEnvelopeData<RevisaoTransferenciaTipo>(response.data);
}

export async function transferirTipoObra(
  obraId: string,
  obraTipoObraId: string,
  body: TransferirTipoObraRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<TransferirTipoObraResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.TRANSFERIR_TIPO_OBRA(obraId, obraTipoObraId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  return readEnvelopeData<TransferirTipoObraResposta>(response.data);
}

export async function previaProjecaoTransferencia(
  obraId: string,
  obraEscopoMacroId: string,
  body: PreviaProjecaoTransferenciaBody,
  { signal }: LookupRequestOptions = {},
): Promise<PreviaProjecaoTransferencia> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.PREVIA_PROJECAO_TRANSFERENCIA(obraId, obraEscopoMacroId),
    body,
    { signal },
  );

  return readEnvelopeData<PreviaProjecaoTransferencia>(response.data);
}

export async function previaProjecaoTransferenciaTipo(
  obraId: string,
  obraTipoObraId: string,
  body: PreviaProjecaoTransferenciaTipoBody,
  { signal }: LookupRequestOptions = {},
): Promise<PreviaProjecaoTransferenciaTipo> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.PREVIA_PROJECAO_TRANSFERENCIA_TIPO(obraId, obraTipoObraId),
    body,
    { signal },
  );

  return readEnvelopeData<PreviaProjecaoTransferenciaTipo>(response.data);
}

export async function getOrigensEscopoMacro(signal?: AbortSignal): Promise<OrigemEscopoMacro[]> {
  const response = await getHttpClient().get<unknown>(FINANCEIRO_OBRAS_PATHS.ORIGENS_ESCOPO_MACRO, {
    signal,
  });

  return extractItems(response.data) as OrigemEscopoMacro[];
}

export async function getEscoposPadraoDoTipo(
  tipoObraId: string,
  signal?: AbortSignal,
): Promise<EscopoPadraoDoTipo[]> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.ESCOPOS_PADRAO_DO_TIPO(tipoObraId),
    { signal },
  );

  return extractItems(response.data) as EscopoPadraoDoTipo[];
}

export async function getEquipamentosSfi(
  obraId: string,
  grupo?: string,
  signal?: AbortSignal,
): Promise<EquipamentosSfi> {
  const termo = grupo?.trim();
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.EQUIPAMENTOS_SFI(obraId),
    { signal, params: termo ? { grupo: termo } : undefined },
  );

  return readEnvelopeData<EquipamentosSfi>(response.data);
}

export async function getNomeComposto103(
  obraId: string,
  { equipamentoSfiId, tipoManutencao }: ParametrosNomeComposto,
  signal?: AbortSignal,
): Promise<NomeComposto103> {
  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_OBRAS_PATHS.NOME_COMPOSTO_103(obraId),
    { signal, params: { equipamentoSfiId, tipoManutencao } },
  );

  return readEnvelopeData<NomeComposto103>(response.data);
}

export async function criarEscopoMacro(
  obraId: string,
  body: CriarEscopoMacroRequest,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CriarEscopoMacroResposta> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_OBRAS_PATHS.ESCOPOS_MACRO_DA_OBRA(obraId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );

  const resposta = readEnvelopeData<CriarEscopoMacroResposta>(response.data);
  const message = isRecord(response.data) ? response.data.message : undefined;
  return typeof message === "string" && message !== "" ? { ...resposta, message } : resposta;
}
